/**
 * The firm's Capital Market Assumptions — the licensee-locked table every projection under this
 * firm runs on (client-profile presets, strategy pricing, Assumptions page, SOA appendices).
 *
 * Edited here by the firm admin, saved onto FirmSettings (merged server-side), applied to the
 * session immediately and re-applied at every sign-in (useProfessionalPortal). Advisers under the
 * firm receive the numbers read-only through the app — no per-client fiddling, no drift.
 *
 * A profile is entered as a PORTFOLIO ONLY — income, growth, franked share. The super and pension
 * columns are DERIVED and shown read-only (pension = income + growth, because pension earnings are
 * tax-free; super = that less the componentised fund tax), so a firm cannot state a super return
 * that contradicts the portfolio beside it. The remaining rule — options must rise Conservative →
 * High growth — is checked by the same shared validator the engine applies.
 */
import React, { useState } from 'react';
import {
    DEFAULT_INVESTMENT_PROFILES,
    DEFAULT_ASSET_CLASS_ASSUMPTIONS,
    INVESTMENT_PROFILE_IDS,
    buildProfileRow,
    validateFirmAssumptions,
    setPersistentFirmAssumptions,
    type AssetClassAssumptions,
    type FirmCapitalMarketAssumptions,
    type FirmCmaProfileRow,
    type InvestmentProfileId,
} from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { FirmSettings } from '../../services/tenantApi.ts';

interface CapitalMarketAssumptionsSettingsProps {
    settings?: FirmSettings;
    firmName?: string;
    setBy?: string;
    onSave?: (patch: FirmSettings) => Promise<boolean>;
}

type DraftRow = { incomeRatePct: string; growthRatePct: string; frankingPct: string };
type AssetDraft = Record<keyof AssetClassAssumptions, string>;

const toProfileDraft = (cma: FirmCapitalMarketAssumptions | null | undefined): Record<InvestmentProfileId, DraftRow> => {
    const out = {} as Record<InvestmentProfileId, DraftRow>;
    for (const id of INVESTMENT_PROFILE_IDS) {
        const personal = cma?.profiles?.[id]?.personal || DEFAULT_INVESTMENT_PROFILES[id].personal;
        out[id] = {
            incomeRatePct: String(personal.incomeRatePct),
            growthRatePct: String(personal.growthRatePct),
            frankingPct: String(personal.frankingPct),
        };
    }
    return out;
};

const toAssetDraft = (cma: FirmCapitalMarketAssumptions | null | undefined): AssetDraft => {
    const out = {} as AssetDraft;
    for (const key of Object.keys(DEFAULT_ASSET_CLASS_ASSUMPTIONS) as (keyof AssetClassAssumptions)[]) {
        const override = cma?.assetClasses?.[key];
        out[key] = String(typeof override === 'number' ? override : DEFAULT_ASSET_CLASS_ASSUMPTIONS[key]);
    }
    return out;
};

const num = (v: string): number => parseFloat(v) || 0;

/** The other asset classes, laid out as the rows the screen shows. */
const ASSET_ROWS: { label: string; note: string; fields: { key: keyof AssetClassAssumptions; head: string }[] }[] = [
    { label: 'Cash / bank accounts', note: 'Interest only — cash does not appreciate.', fields: [{ key: 'bankIncomePct', head: 'Interest %' }] },
    { label: 'Term deposits', note: 'Interest only.', fields: [{ key: 'termDepositIncomePct', head: 'Interest %' }] },
    {
        label: 'Shares / managed funds (personally held)',
        note: 'Income is taxed each year at the client’s own rates; growth is taxed as CGT on sale.',
        fields: [{ key: 'sharesIncomePct', head: 'Income %' }, { key: 'sharesGrowthPct', head: 'Growth %' }, { key: 'sharesFrankingPct', head: 'Franked %' }],
    },
    {
        label: 'Investment property',
        note: 'Gross rent as a % of value; expenses as a % of that rent.',
        fields: [{ key: 'propertyYieldPct', head: 'Gross yield %' }, { key: 'propertyExpenseRatioPct', head: 'Expenses % of rent' }, { key: 'propertyGrowthPct', head: 'Growth %' }],
    },
];

export function CapitalMarketAssumptionsSettings({ settings, firmName, setBy, onSave }: CapitalMarketAssumptionsSettingsProps) {
    const saved = (settings?.capitalMarketAssumptions as FirmCapitalMarketAssumptions | null | undefined) ?? null;
    const [profileDraft, setProfileDraft] = useState<Record<InvestmentProfileId, DraftRow>>(() => toProfileDraft(saved));
    const [assetDraft, setAssetDraft] = useState<AssetDraft>(() => toAssetDraft(saved));
    const [problems, setProblems] = useState<string[]>([]);
    const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle');

    const buildCma = (): FirmCapitalMarketAssumptions => {
        const profiles: Partial<Record<InvestmentProfileId, FirmCmaProfileRow>> = {};
        for (const id of INVESTMENT_PROFILE_IDS) {
            const d = profileDraft[id];
            profiles[id] = { personal: { incomeRatePct: num(d.incomeRatePct), growthRatePct: num(d.growthRatePct), frankingPct: num(d.frankingPct) } };
        }
        const assetClasses = {} as AssetClassAssumptions;
        for (const key of Object.keys(DEFAULT_ASSET_CLASS_ASSUMPTIONS) as (keyof AssetClassAssumptions)[]) {
            assetClasses[key] = num(assetDraft[key]);
        }
        return {
            profiles,
            assetClasses,
            effectiveDate: new Date().toISOString().slice(0, 10),
            setBy: setBy || undefined,
            firmName: firmName || undefined,
        };
    };

    const save = async () => {
        const cma = buildCma();
        const found = validateFirmAssumptions(cma);
        setProblems(found);
        if (found.length > 0 || !onSave) return;
        setState('saving');
        const ok = await onSave({ capitalMarketAssumptions: cma });
        if (ok) setPersistentFirmAssumptions(cma); // the session prices on the new table immediately
        setState(ok ? 'saved' : 'failed');
    };

    const resetToDefaults = async () => {
        setProfileDraft(toProfileDraft(null));
        setAssetDraft(toAssetDraft(null));
        setProblems([]);
        if (!onSave) return;
        setState('saving');
        const ok = await onSave({ capitalMarketAssumptions: null });
        if (ok) setPersistentFirmAssumptions(null);
        setState(ok ? 'saved' : 'failed');
    };

    const input: React.CSSProperties = { width: '100%', maxWidth: 92, padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, textAlign: 'right' };
    const th: React.CSSProperties = { fontSize: 11, letterSpacing: '.02em', textTransform: 'uppercase', color: '#64748b', fontWeight: 600, textAlign: 'right', padding: '0 8px 8px', whiteSpace: 'nowrap' };
    const thLeft: React.CSSProperties = { ...th, textAlign: 'left', paddingLeft: 0 };
    const derived: React.CSSProperties = { fontSize: 13.5, fontWeight: 700, color: '#0f172a', textAlign: 'right', padding: '0 8px', whiteSpace: 'nowrap' };
    const rowLabel: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#0f172a', paddingRight: 14, minWidth: 150 };

    return (
        <div style={{ marginTop: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Capital market assumptions</div>
            <p style={{ fontSize: 13, color: '#64748b', margin: '6px 0 4px', lineHeight: 1.55, maxWidth: 720 }}>
                Every projection your firm&apos;s advisers and clients see runs on these figures — client-profile presets, the returns
                strategies invest at, the Assumptions page and SOA appendices. Set once, applied firm-wide, disclosed on advice.
            </p>
            <p style={{ fontSize: 12.5, color: saved ? '#0f172a' : '#64748b', margin: '0 0 16px' }}>
                {saved?.effectiveDate
                    ? `Your firm's assumptions are in force — effective ${saved.effectiveDate}${saved.setBy ? `, set by ${saved.setBy}` : ''}.`
                    : 'Currently using the AgedCareWise defaults.'}
            </p>

            <div style={{ fontWeight: 600, fontSize: 13, color: '#334155', marginBottom: 2 }}>Superannuation &amp; pension — by investment option</div>
            <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 10px', lineHeight: 1.5, maxWidth: 720 }}>
                Enter the PORTFOLIO for each option. The super and pension columns are worked out from it: pension earnings are
                tax-free, so pension = income + growth; super is that less the fund&apos;s tax on income (15%, after franking credits)
                and growth (10%).
            </p>
            <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 720 }}>
                    <thead>
                        <tr>
                            <th style={thLeft}>Option</th>
                            <th style={th}>Income %</th>
                            <th style={th}>Growth %</th>
                            <th style={th}>Franked %</th>
                            <th style={{ ...th, color: '#0f172a' }}>Super %</th>
                            <th style={{ ...th, color: '#0f172a' }}>Pension %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {INVESTMENT_PROFILE_IDS.map((id) => {
                            const d = profileDraft[id];
                            const row = buildProfileRow(DEFAULT_INVESTMENT_PROFILES[id].label, {
                                incomeRatePct: num(d.incomeRatePct), growthRatePct: num(d.growthRatePct), frankingPct: num(d.frankingPct),
                            });
                            const set = (field: keyof DraftRow) => (e: React.ChangeEvent<HTMLInputElement>) =>
                                setProfileDraft((prev) => ({ ...prev, [id]: { ...prev[id], [field]: e.target.value } }));
                            return (
                                <tr key={id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                    <td style={{ ...rowLabel, padding: '8px 14px 8px 0' }}>{DEFAULT_INVESTMENT_PROFILES[id].label}</td>
                                    <td style={{ padding: '6px 8px' }}><input aria-label={`${id} income`} style={input} value={d.incomeRatePct} onChange={set('incomeRatePct')} /></td>
                                    <td style={{ padding: '6px 8px' }}><input aria-label={`${id} growth`} style={input} value={d.growthRatePct} onChange={set('growthRatePct')} /></td>
                                    <td style={{ padding: '6px 8px' }}><input aria-label={`${id} franking`} style={input} value={d.frankingPct} onChange={set('frankingPct')} /></td>
                                    <td style={derived}>{row.accumulationNetPct.toFixed(2)}</td>
                                    <td style={derived}>{row.pensionNetPct.toFixed(2)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div style={{ fontWeight: 600, fontSize: 13, color: '#334155', margin: '22px 0 10px' }}>Other asset classes</div>
            <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 720 }}>
                    <tbody>
                        {ASSET_ROWS.map((assetRow) => (
                            <tr key={assetRow.label} style={{ borderTop: '1px solid #f1f5f9' }}>
                                <td style={{ ...rowLabel, padding: '10px 14px 10px 0', verticalAlign: 'top' }}>
                                    {assetRow.label}
                                    <div style={{ fontWeight: 400, fontSize: 11.5, color: '#94a3b8', marginTop: 3, maxWidth: 240, lineHeight: 1.45 }}>{assetRow.note}</div>
                                </td>
                                {assetRow.fields.map((f) => (
                                    <td key={f.key} style={{ padding: '10px 8px', verticalAlign: 'top' }}>
                                        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.02em', color: '#64748b', fontWeight: 600, marginBottom: 4 }}>{f.head}</div>
                                        <input
                                            aria-label={f.key}
                                            style={input}
                                            value={assetDraft[f.key]}
                                            onChange={(e) => setAssetDraft((prev) => ({ ...prev, [f.key]: e.target.value }))}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {problems.length > 0 && (
                <div style={{ marginTop: 14, background: '#fff', border: '1px solid #e2e8f0', borderLeft: '4px solid #ef4444', borderRadius: 8, padding: '10px 14px' }}>
                    {problems.map((p) => <p key={p} style={{ fontSize: 12.5, color: '#b91c1c', margin: '2px 0' }}>{p}</p>)}
                </div>
            )}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 18, flexWrap: 'wrap' }}>
                <button type="button" onClick={save} disabled={state === 'saving'} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: '#1a6fd4', color: '#fff', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }}>
                    {state === 'saving' ? 'Saving…' : 'Save firm assumptions'}
                </button>
                <button type="button" onClick={resetToDefaults} disabled={state === 'saving'} style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                    Use AgedCareWise defaults
                </button>
                {state === 'saved' && <span style={{ fontSize: 12.5, color: '#15803d', fontWeight: 600 }}>Saved — applied to this session and every sign-in.</span>}
                {state === 'failed' && <span style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 600 }}>Couldn&apos;t save — try again.</span>}
            </div>
        </div>
    );
}

export default CapitalMarketAssumptionsSettings;
